/**
 * MembersTab — per-project membership (direct + inherited).
 * Adding a member by email: super_admin/admin uses /api/auth/invite (later).
 * For now: pick from existing profiles by email/name.
 */
import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Stack, TextField, Chip, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useProjectMembers, useCreateProjectMember, useDeleteProjectMember } from '../../hooks/useProjects';
import { api } from '../../lib/api';

export default function MembersTab({ projectId }) {
  const { data, isLoading } = useProjectMembers(projectId);
  const [adding, setAdding] = useState(false);
  const deleteMut = useDeleteProjectMember();

  if (isLoading) return <Typography variant="body2">Loading…</Typography>;

  const direct = data?.direct || [];
  const inherited = data?.inherited || [];

  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ flex: 1 }}>Members</Typography>
        <Button size="small" startIcon={<AddIcon />} variant="contained" onClick={() => setAdding(true)}>
          Invite / Add member
        </Button>
      </Stack>

      {/* Direct */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2">Directly assigned ({direct.length})</Typography>
        </Box>
        {direct.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No one assigned at this level. Use Invite above, or assign at a higher level (season/week) to cascade.
          </Typography>
        )}
        {direct.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell>Added</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {direct.map((m) => (
                <TableRow key={m.profile_id} hover>
                  <TableCell>
                    {m.profile_name || '—'}
                    {m.is_external && <Chip size="small" label="External" sx={{ ml: 1 }} color="info" />}
                  </TableCell>
                  <TableCell>{m.profile_email || '—'}</TableCell>
                  <TableCell>{m.project_role || '—'}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {m.hourly_rate != null ? `$${Number(m.hourly_rate).toFixed(2)}` : '—'}
                  </TableCell>
                  <TableCell>{m.added_at ? new Date(m.added_at).toLocaleDateString() : '—'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Remove member">
                      <IconButton size="small" onClick={() => {
                        if (confirm(`Remove ${m.profile_name || m.profile_email} from this project?`)) {
                          deleteMut.mutate({ project_id: projectId, profile_id: m.profile_id });
                        }
                      }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Inherited */}
      {inherited.length > 0 && (
        <Paper variant="outlined">
          <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2">Inherited from parent ({inherited.length})</Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell>Inherited from</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inherited.map((m) => (
                <TableRow key={`${m.project_id}-${m.profile_id}`}>
                  <TableCell>
                    {m.profile_name || '—'}
                    {m.is_external && <Chip size="small" label="External" sx={{ ml: 1 }} color="info" />}
                  </TableCell>
                  <TableCell>{m.project_role || '—'}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {m.hourly_rate != null ? `$${Number(m.hourly_rate).toFixed(2)}` : '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{m.via_project_name || m.project_id}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <AddMemberDialog
        open={adding}
        onClose={() => setAdding(false)}
        projectId={projectId}
      />
    </Box>
  );
}

function AddMemberDialog({ open, onClose, projectId }) {
  const createMut = useCreateProjectMember();
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState('');
  const [projectRole, setProjectRole] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');

  // Load profiles when dialog opens
  useEffect(() => {
    if (!open) return;
    api.get('/profiles').then((rows) => {
      setProfiles(Array.isArray(rows) ? rows : []);
    }).catch(() => setProfiles([]));
  }, [open]);

  const submit = async () => {
    if (!profileId) return;
    await createMut.mutateAsync({
      project_id: projectId,
      profile_id: profileId,
      project_role: projectRole || null,
      hourly_rate: hourlyRate === '' ? null : Number(hourlyRate),
    });
    setProfileId(''); setProjectRole(''); setHourlyRate('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add member</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label="Profile"
            value={profileId}
            onChange={(e) => setProfileId(e.target.value)}
            fullWidth
            helperText="Pick an existing profile. To invite a new external user, use the Invite flow in Team settings."
          >
            <MenuItem value="">—</MenuItem>
            {profiles.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name || p.email} {p.is_external ? '· External' : ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Project role"
            value={projectRole}
            onChange={(e) => setProjectRole(e.target.value)}
            fullWidth
            placeholder="editor, colorist, producer…"
          />
          <TextField
            label="Hourly rate ($)"
            type="number"
            value={hourlyRate}
            onChange={(e) => setHourlyRate(e.target.value)}
            inputProps={{ min: 0, step: 0.5 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={!profileId || createMut.isPending}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
